import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Stack,
  IconButton,
  CircularProgress,
  Chip,
  alpha,
  Divider,
} from "@mui/material";
import {
  CloudUpload,
  Save,
  Add,
  Delete,
  VideoLibrary,
  Image,
  Public,
} from "@mui/icons-material";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const GOLD = "#D4AF37";
const API_BASE_URL = "https://tdtlworld.com/dss-kaas-backend/";

export default function CourseLandingPage({ courseId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [courseData, setCourseData] = useState({
    title: "",
    subtitle: "",
    description: "",
    price: "",
    cover_img: null,
    trailer: null,
    categories: [],
  });
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const token = localStorage.getItem("jwttoken");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [courseResp, categoryResp] = await Promise.all([
          axios.get(`${API_BASE_URL}lms/courses/${courseId}/`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API_BASE_URL}lms/categories/`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        const data = courseResp.data;
        setCourseData({
          title: data.title || "",
          subtitle: data.subtitle || "",
          description: data.description || "",
          price: data.price || "",
          cover_img: data.cover_img || null,
          trailer: data.trailer || null,
          categories: data.categories || [],
        });
        setAllCategories(categoryResp.data || []);
      } catch (err) {
        console.error("Fetch failed", err);
      } finally {
        setLoading(false);
      }
    };

    if (courseId) fetchData();
  }, [courseId, token]);

  const handleFileUpload = (event, field) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setCourseData(prev => ({ ...prev, [field]: reader.result }));
      };
    }
  };

  const addCategory = () => {
    if (selectedCategory && !courseData.categories.includes(selectedCategory)) {
      setCourseData(prev => ({ 
        ...prev, 
        categories: [...prev.categories, selectedCategory] 
      }));
      setSelectedCategory("");
    }
  };

  const removeCategory = (cat) => {
    setCourseData(prev => ({ 
      ...prev, 
      categories: prev.categories.filter(c => c !== cat) 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.patch(
        `${API_BASE_URL}lms/courses/${courseId}/`,
        courseData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Course landing page updated",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Update Failed" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto" }}>
      <Typography variant="h4" fontWeight={900} gutterBottom>
        Course Landing Page
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 6 }}>
        Your course landing page is your pitch to potential students. Make it compelling, clear, and professional.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Stack spacing={4}>
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Basic Info</Typography>
            <Divider sx={{ mb: 3 }} />
            <Stack spacing={3}>
              <TextField 
                fullWidth 
                label="Course Title" 
                value={courseData.title}
                onChange={(e) => setCourseData({ ...courseData, title: e.target.value })}
                variant="outlined"
              />
              <TextField 
                fullWidth 
                label="Course Subtitle" 
                placeholder="Ex: Master React.js from scratch with hooks and redux."
                value={courseData.subtitle}
                onChange={(e) => setCourseData({ ...courseData, subtitle: e.target.value })}
              />
              <TextField 
                fullWidth 
                multiline 
                rows={6} 
                label="Course Description" 
                value={courseData.description}
                onChange={(e) => setCourseData({ ...courseData, description: e.target.value })}
              />
              <TextField 
                label="Price (INR)" 
                sx={{ maxWidth: 200 }}
                value={courseData.price}
                onChange={(e) => setCourseData({ ...courseData, price: e.target.value })}
              />
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Taxonomy</Typography>
            <Divider sx={{ mb: 3 }} />
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                select
                fullWidth
                label="Select Category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                SelectProps={{ native: true }}
              >
                <option value=""></option>
                {allCategories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </TextField>
              <Button 
                variant="outlined" 
                startIcon={<Add />} 
                onClick={addCategory}
                sx={{ borderColor: GOLD, color: GOLD, fontWeight: 700, px: 4 }}
              >
                Add
              </Button>
            </Stack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {courseData.categories.map(cat => (
                <Chip 
                  key={cat} 
                  label={cat} 
                  onDelete={() => removeCategory(cat)}
                  sx={{ bgcolor: alpha(GOLD, 0.1), fontWeight: 600, border: `1px solid ${GOLD}` }}
                />
              ))}
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Course Media</Typography>
            <Divider sx={{ mb: 3 }} />
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>Course Image</Typography>
                <Box 
                  sx={{ 
                    height: 200, 
                    borderRadius: 3, 
                    bgcolor: "action.hover", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  {courseData.cover_img ? (
                    <Box component="img" src={courseData.cover_img} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <Image sx={{ fontSize: 60, color: "divider" }} />
                  )}
                  <Box sx={{ position: "absolute", bottom: 10, right: 10 }}>
                    <input accept="image/*" id="cover-upload" type="file" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "cover_img")} />
                    <label htmlFor="cover-upload">
                      <Button variant="contained" component="span" size="small" startIcon={<CloudUpload />} sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "#eee" } }}>
                        Upload
                      </Button>
                    </label>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>Promo Video (Trailer)</Typography>
                <Box 
                  sx={{ 
                    height: 200, 
                    borderRadius: 3, 
                    bgcolor: "action.hover", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider"
                  }}
                >
                  {courseData.trailer ? (
                    <Box component="video" src={courseData.trailer} controls sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    <VideoLibrary sx={{ fontSize: 60, color: "divider" }} />
                  )}
                  <Box sx={{ position: "absolute", bottom: 10, right: 10 }}>
                    <input accept="video/*" id="trailer-upload" type="file" style={{ display: "none" }} onChange={(e) => handleFileUpload(e, "trailer")} />
                    <label htmlFor="trailer-upload">
                      <Button variant="contained" component="span" size="small" startIcon={<CloudUpload />} sx={{ bgcolor: "white", color: "black", "&:hover": { bgcolor: "#eee" } }}>
                        Upload
                      </Button>
                    </label>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Save />}
              sx={{ 
                py: 1.5, 
                px: 6, 
                borderRadius: "50px", 
                bgcolor: GOLD, 
                fontWeight: 800,
                fontSize: "1rem",
                "&:hover": { bgcolor: alpha(GOLD, 0.8) }
              }}
            >
              {submitting ? "Updating..." : "Update Course"}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}
